/**
 * DateRangeFilter Component
 *
 * A single calendar popover that captures a from/to date range.
 * Built on MUI DateCalendar with HAAT styling. First click sets the start,
 * second click sets the end (auto-swapped if picked in reverse order).
 */

import React, { useMemo, useState } from 'react';
import { Popover } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import { format, isAfter, isBefore, isSameDay, startOfDay } from 'date-fns';
import { Calendar, ChevronDown, X } from 'lucide-react';

// =============================================================================
// TYPES
// =============================================================================

export interface DateRangeValue {
  from: Date | null;
  to: Date | null;
}

export interface DateRangeFilterProps {
  /** Current range value */
  value: DateRangeValue;
  /** Change handler */
  onChange: (value: DateRangeValue) => void;
  /** Trigger label (e.g. "Created") */
  label?: string;
  /** Placeholder shown when no range is selected */
  placeholder?: string;
  /** Minimum selectable date */
  minDate?: Date;
  /** Maximum selectable date */
  maxDate?: Date;
}

const ACCENT_COLOR = 'rgb(var(--color-primary-500))';

// =============================================================================
// RANGE DAY
// =============================================================================

interface RangeDayProps extends PickersDayProps {
  rangeFrom: Date | null;
  rangeTo: Date | null;
}

const RangeDay: React.FC<RangeDayProps> = ({ rangeFrom, rangeTo, day, ...other }) => {
  const isStart = rangeFrom ? isSameDay(day, rangeFrom) : false;
  const isEnd = rangeTo ? isSameDay(day, rangeTo) : false;
  const isInRange =
    rangeFrom && rangeTo
      ? (isAfter(day, rangeFrom) || isSameDay(day, rangeFrom)) &&
        (isBefore(day, rangeTo) || isSameDay(day, rangeTo))
      : false;
  const isEndpoint = isStart || isEnd;

  return (
    <PickersDay
      {...other}
      day={day}
      selected={isEndpoint}
      sx={{
        ...(isInRange && !isEndpoint
          ? {
              backgroundColor: 'rgba(var(--color-primary-500), 0.12)',
              borderRadius: 0,
            }
          : {}),
        ...(isEndpoint
          ? {
              backgroundColor: `${ACCENT_COLOR} !important`,
              color: '#fff',
            }
          : {}),
      }}
    />
  );
};

// =============================================================================
// COMPONENT
// =============================================================================

export const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  value,
  onChange,
  label = 'Date',
  placeholder = 'All dates',
  minDate,
  maxDate,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const isOpen = Boolean(anchorEl);
  const hasValue = Boolean(value.from || value.to);

  const displayValue = useMemo(() => {
    if (!hasValue) return placeholder;
    const fromLabel = value.from ? format(value.from, 'dd MMM') : '…';
    const toLabel = value.to ? format(value.to, 'dd MMM') : '…';
    return `${fromLabel} - ${toLabel}`;
  }, [value, hasValue, placeholder]);

  const handleDayChange = (day: Date | null) => {
    if (!day) return;
    const picked = startOfDay(day);

    // No start yet, or a full range already set -> begin a new range.
    if (!value.from || (value.from && value.to)) {
      onChange({ from: picked, to: null });
      return;
    }

    // Have a start, pick the end (swap if earlier than start).
    if (isBefore(picked, value.from)) {
      onChange({ from: picked, to: value.from });
    } else {
      onChange({ from: value.from, to: picked });
    }
    setAnchorEl(null);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange({ from: null, to: null });
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        className={`
          inline-flex items-center gap-2
          px-3 py-2 rounded-xl
          text-sm font-medium
          border-2 transition-all duration-200
          ${hasValue
            ? 'bg-primary-500/10 border-primary-500/30 text-primary-500'
            : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
          }
          ${isOpen ? 'ring-2 ring-primary-500/20' : ''}
        `}
      >
        <span className={hasValue ? 'text-primary-500' : 'text-gray-400'}>
          <Calendar size={14} />
        </span>
        <span className="max-w-[120px] truncate">{label}</span>
        {hasValue && (
          <>
            <span className="text-gray-300">|</span>
            <span className="font-semibold">{displayValue}</span>
          </>
        )}
        <ChevronDown
          size={14}
          className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
        {hasValue && (
          <span
            role="button"
            tabIndex={0}
            onClick={handleClear}
            className="ml-1 p-0.5 rounded-full hover:bg-primary-500/20 transition-colors"
          >
            <X size={12} />
          </span>
        )}
      </button>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              borderRadius: '12px',
              border: '1px solid #e5e7eb',
              boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
            },
          },
        }}
      >
        <div className="px-3 pt-3 flex items-center justify-between">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            {label}
          </span>
          <span className="text-xs text-gray-500">{hasValue ? displayValue : 'Pick start & end'}</span>
        </div>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DateCalendar
            value={value.to ?? value.from ?? null}
            onChange={handleDayChange}
            minDate={minDate}
            maxDate={maxDate}
            slots={{ day: RangeDay as React.ComponentType<PickersDayProps> }}
            slotProps={{
              day: { rangeFrom: value.from, rangeTo: value.to } as Partial<RangeDayProps>,
            }}
          />
        </LocalizationProvider>
        <div className="px-3 pb-3 flex justify-end">
          <button
            type="button"
            onClick={() => onChange({ from: null, to: null })}
            className="text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors"
          >
            Clear
          </button>
        </div>
      </Popover>
    </div>
  );
};

export default DateRangeFilter;
